import { MapPin } from 'lucide-react';
import { useCountryStore } from '../../shared/country/countryStore';
import { useI18nStore } from '../../shared/i18n/i18nStore';

/**
 * The country a new account is about to be bound to, shown directly above the
 * submit button.
 *
 * Registration is the only write of `users.country_code`, and that column is
 * immutable — a player who registers under the wrong country cannot be migrated
 * (wrong currency, wrong payment rail, and the referral tree is an in-table FK).
 * See doc/MULTI_COUNTRY_ROLLOUT_CHECKLIST.md §3.3 and §4.
 *
 * Two deliberate choices from that design:
 *
 * - **Currency is shown, not just the country.** Players are often indifferent
 *   to "India" but never to whether their balance is INR or BRL, so the
 *   currency is the line that actually catches a wrong guess.
 * - **This is not a checkbox.** A confirmation checkbox gets ticked without
 *   being read; a country name sitting above the button gets read. The existing
 *   submit button *is* the confirmation, which keeps the funnel at one tap.
 *
 * No "change country" button here (Task 10) — `<CountryPicker />` now lives in
 * the form itself, right beside the phone field it governs, so this notice does
 * not need to be a second entry point into the same choice. That also means
 * this is the one place a player can tell the picker decides their *country*
 * (currency, payment rail) and not merely a phone dial code: the currency line
 * plus the "cannot be changed later" line below are what carry that, since the
 * picker itself only ever shows a flag and a `+55`-shaped chip.
 */
export function RegisterCountryNotice() {
  const t = useI18nStore((state) => state.t);
  const country = useCountryStore((state) => state.country);
  const loaded = useCountryStore((state) => state.loaded);

  return (
    <div className="auth-country-notice" data-register-country-notice="true">
      <div className="auth-country-notice-head">
        <MapPin size={13} className="shrink-0" aria-hidden="true" />
        <span className="auth-country-notice-label">{t('registerCountryLabel')}</span>
        <span className="auth-country-code" aria-hidden="true">{country.code}</span>
        <strong className="auth-country-name">{country.name}</strong>
      </div>

      <div className="auth-country-notice-meta">
        <span>
          {t('registerCountryCurrency')} <strong>{country.fiatCurrency}</strong>
        </span>
        <span className="auth-country-notice-dot" aria-hidden="true">·</span>
        <span>{t('registerCountryImmutable')}</span>
      </div>

      {/*
        Only when the lookup itself failed. Having several active countries is the
        normal state under one domain — the player chooses, so there is nothing
        unconfirmed to warn about. Warning on that would have put a permanent
        alarm on every registration.
      */}
      {!loaded ? (
        <p className="auth-country-unverified">{t('registerCountryUnverified')}</p>
      ) : null}
    </div>
  );
}
